import { z } from 'zod';
/**
 * Why a sign-in did not happen, in terms both apps can render.
 *
 * Shared rather than duplicated because the admin and the mobile app have the same
 * acceptance criterion — a typed error, never a raw provider string — and would
 * otherwise each grow their own string-matching over supabase-js internals. This
 * package already crosses all three consumers and carries no platform dependencies,
 * which makes it the only shared home that does not invent a package.
 */
export const authErrorCodeSchema = z.enum([
    'BAD_CREDENTIALS',
    'EMAIL_NOT_CONFIRMED',
    'RATE_LIMITED',
    'WEAK_PASSWORD',
    'ALREADY_REGISTERED',
    'CANCELLED',
    'OFFLINE',
    'UNKNOWN',
]);
/**
 * A sign-in the person abandoned.
 *
 * Cancellation is not a Supabase error at all — the OAuth browser sheet simply
 * closes — so it needs a shape of its own to be told apart from a failure. The
 * difference matters: a failure gets a message, a cancellation gets silence.
 */
export class AuthCancelledError extends Error {
    constructor() {
        super('The sign-in was cancelled.');
        this.name = 'AuthCancelledError';
    }
}
/** Supabase's own `code` values, which are the reliable discriminator when present. */
const BY_PROVIDER_CODE = {
    invalid_credentials: 'BAD_CREDENTIALS',
    email_not_confirmed: 'EMAIL_NOT_CONFIRMED',
    over_request_rate_limit: 'RATE_LIMITED',
    over_email_send_rate_limit: 'RATE_LIMITED',
    over_sms_send_rate_limit: 'RATE_LIMITED',
    weak_password: 'WEAK_PASSWORD',
    user_already_exists: 'ALREADY_REGISTERED',
    email_exists: 'ALREADY_REGISTERED',
};
const MESSAGES = {
    // Deliberately does not say whether the address has an account. A form that
    // distinguishes the two is a way to find out who has one.
    BAD_CREDENTIALS: 'That email and password do not match.',
    EMAIL_NOT_CONFIRMED: 'Confirm your email address, then sign in.',
    RATE_LIMITED: 'Too many attempts. Wait a minute and try again.',
    WEAK_PASSWORD: 'Choose a longer password.',
    ALREADY_REGISTERED: 'That email already has an account. Sign in instead.',
    CANCELLED: 'Sign-in was cancelled.',
    OFFLINE: 'No connection. Check your network and try again.',
    UNKNOWN: 'Something went wrong signing you in. Try again.',
};
function read(error, key) {
    return error[key];
}
/**
 * Classify anything thrown during sign-in.
 *
 * Never throws and never returns undefined: this runs in a catch block, and a
 * classifier that can itself fail turns one failure into two.
 */
export function toAuthErrorCode(error) {
    if (error instanceof AuthCancelledError)
        return 'CANCELLED';
    if (typeof error !== 'object' || error === null)
        return 'UNKNOWN';
    const name = read(error, 'name');
    // supabase-js wraps a failed fetch rather than letting it through, so the
    // offline case has a name of its own before it has anything else.
    if (name === 'AuthRetryableFetchError')
        return 'OFFLINE';
    // A bare TypeError is what a browser and React Native each throw when the
    // request never left the device. The two runtimes word it differently.
    if (error instanceof TypeError) {
        const message = String(read(error, 'message'));
        if (/failed to fetch|network request failed|load failed/i.test(message)) {
            return 'OFFLINE';
        }
    }
    const providerCode = read(error, 'code');
    if (typeof providerCode === 'string' && providerCode in BY_PROVIDER_CODE) {
        return BY_PROVIDER_CODE[providerCode] ?? 'UNKNOWN';
    }
    // Older builds of supabase-js send no `code` at all. The status is coarser but
    // right about the one case a person can act on.
    if (read(error, 'status') === 429)
        return 'RATE_LIMITED';
    return 'UNKNOWN';
}
/** What to show. One place, so a rewrite later is one file. */
export function authErrorMessage(code) {
    return MESSAGES[code];
}
//# sourceMappingURL=auth-errors.js.map